import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Play, X, Clock, Video } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

const LiveInvitePopup = ({ isOpen, invite, onClose, onAccept, onDecline }) => {
  const [countdown, setCountdown] = useState(30);
  const navigate = useNavigate();
  const countdownRef = useRef(30);

  const handleDecline = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_URL}/api/notifications/live-invites/${invite?.id}/respond`,
        null,
        { 
          params: { action: 'decline' },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
    } catch (err) {
      console.error('Failed to decline invite:', err);
    }
    
    if (onDecline) onDecline(invite);
    onClose();
  }, [invite, onDecline, onClose]);

  // Reset countdown when invite changes
  useEffect(() => {
    if (isOpen && invite?.id) {
      countdownRef.current = 30;
      setCountdown(30);
    }
  }, [isOpen, invite?.id]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setInterval(() => {
      countdownRef.current -= 1;
      setCountdown(countdownRef.current);
      
      if (countdownRef.current <= 0) {
        clearInterval(timer);
        handleDecline();
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isOpen, handleDecline]);

  const handleAccept = async () => {
    // Delegate entirely to parent's onAccept handler
    // The parent (App.js) handles the API call and navigation logic
    // based on whether the user is already broadcasting or not
    try {
      if (onAccept) {
        await onAccept(invite);
      }
      onClose();
    } catch (err) {
      console.error('Accept handler error:', err);
      onClose();
    }
  };

  if (!invite) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDecline()}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-purple-900 to-slate-900 border-purple-500/50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center animate-pulse">
              <Play className="w-4 h-4 text-white" />
            </div>
            Live Stream Invite!
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Sender Info */}
          <div className="flex items-center gap-4 p-4 bg-black/30 rounded-lg">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-xl font-bold">
              {invite.sender_name?.[0] || '?'}
            </div>
            <div>
              <p className="text-white font-bold text-lg">{invite.sender_name}</p>
              <p className="text-purple-300 text-sm">wants you to join their stream</p>
            </div>
          </div>
          
          {/* Stream Info */}
          {invite.stream_title && (
            <div className="flex items-center gap-3 p-3 bg-purple-800/30 rounded-lg">
              <Video className="w-5 h-5 text-red-400" />
              <span className="text-white">{invite.stream_title}</span>
            </div>
          )}
          
          {/* Countdown Timer */}
          <div className="flex items-center justify-center gap-2 py-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm">
              Auto-declining in {countdown}s
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleDecline}
              variant="outline"
              className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <X className="w-4 h-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 animate-pulse"
            >
              <Play className="w-4 h-4 mr-2" />
              Join Stream
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hook to poll for live invites
// Polls when user is logged in, regardless of whether they're live
// This ensures invites are received even when not broadcasting
export const useLiveInvites = (userId, shouldPoll = true) => {
  const [pendingInvite, setPendingInvite] = useState(null);
  const [showInvitePopup, setShowInvitePopup] = useState(false);

  useEffect(() => {
    // Poll whenever user is logged in (not just when live)
    if (!userId || !shouldPoll) return;

    const pollInvites = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        
        const response = await axios.get(`${API_URL}/api/notifications/live-invites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const invites = response.data.invites || [];
        if (invites.length > 0 && !showInvitePopup) {
          // Only show popup if we don't already have one
          console.log('🔔 New co-host invite received via polling:', invites[0]);
          setPendingInvite(invites[0]);
          setShowInvitePopup(true);
        }
      } catch (err) {
        // Silent fail - don't spam console
        if (err.response?.status !== 401) {
          console.log('Poll invites error:', err.message);
        }
      }
    };

    // POLL WIDENED (Feb 2026): 'live_invite' events over the Universal
    // Sync WebSocket are the primary delivery mechanism. This REST poll
    // is a fallback for when the WS drops, bumped 3s → 15s so we don't
    // pound the API from every logged-in tab. Happy path stays instant
    // via WS; a dropped socket still recovers within one interval.
    pollInvites();
    const interval = setInterval(pollInvites, 15000);
    return () => clearInterval(interval);
  }, [userId, shouldPoll, showInvitePopup]);

  const closeInvitePopup = () => {
    setShowInvitePopup(false);
    setPendingInvite(null);
  };

  return {
    pendingInvite,
    showInvitePopup,
    closeInvitePopup,
    setPendingInvite
  };
};

export default LiveInvitePopup;
